import axios from "axios";
import { HttpClient, RequestParams } from "../../utils/api/http-client";
import IBaseListingResponse from "../../utils/api/base-listing-response";

import { ACCESS_TOKEN, LANGUAGE_CODE } from "../../utils/helpers/constants";

import {
  ICreateOrder,
  IExportOrderExcel,
  IMarkAccountingFinishedRequest,
  IUpdateOrder,
  IUpdateDeliveryMode,
  Update,
} from "../../models/order/request";
import {
    IOrder,
    IOrderDetails,
    IOrderShort, IRatedOrder,
} from "../../models/order/response";
import { IOrderQuery } from "../../models/order/query";
import { IOrderHistory } from "../../models/order/order-history/response";
import { IOrderStatus } from "../../models/order/order-status/response";
import { IUpdateOrderCustomerSatisfactionRequest } from "../../models/customer-satisfaction/request";
import {
  ICustomerSatisfactionAudit,
  IOrderCustomerSatisfaction,
} from "../../models/customer-satisfaction/response";
import { ICustomerSatisfactionAuditQuery } from "../../models/customer-satisfaction/query";

class OrderService<SecurityDataType = unknown> {
  http: HttpClient<SecurityDataType>;

  constructor(http: HttpClient<SecurityDataType>) {
    this.http = http;
  }

  getAllOrders = (query: IOrderQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IOrder>>({
      path: `/order`,
      method: "GET",
      query,
      ...params,
    });

  getAllOrdersShort = (query: IOrderQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IOrderShort>>({
      path: `/orders-short`,
      method: "GET",
      query,
      ...params,
    });

  getOrder = (id: number, params?: RequestParams) =>
    this.http.request<IOrderDetails>({
      path: `/order/${id}`,
      method: "GET",
      ...params,
    });
  getOrderHistories = (
    id: number,
    query: IOrderQuery,
    params?: RequestParams
  ) =>
    this.http.request<IBaseListingResponse<IOrderHistory>>({
      path: `/order/histories/${id}`,
      method: "GET",
      query,
      ...params,
    });

  getOrderStatus = (id: number, query: IOrderQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IOrderStatus>>({
      path: `/order/status/${id}`,
      method: "GET",
      query,
      ...params,
    });

  updateOrder = (id: number, data: Update, params?: RequestParams) =>
    this.http.request<IOrder>({
      path: `/order/update/${id}`,
      method: "POST",
      body: data,
      ...params,
    });

  exportExcel = (data: IExportOrderExcel, query: IOrderQuery) =>
    axios.post(` ${process.env.REACT_APP_BASE_API_URL}/order/export`, data, {
      headers: {
        authorization: `Bearer ${
          localStorage.getItem(ACCESS_TOKEN) ||
          sessionStorage.getItem(ACCESS_TOKEN)
        }`,
        "Content-Type": "application/json",
        "accept-language": `${
          localStorage.getItem(LANGUAGE_CODE) ??
          process.env.REACT_APP_DEFAULT_LANGUAGE
        }`,
      },
      params: query,
      responseType: "arraybuffer",
    });

    getRatedOrders = (query: IOrderQuery, params?: RequestParams) =>
        this.http.request<IBaseListingResponse<IRatedOrder>>({
            path: `/rated-orders`,
            method: "GET",
            query,
            ...params,
        });

  confirmPayment = (id: number, params?: RequestParams) =>
    this.http.request<IOrderDetails>({
      path: `/order/${id}/confirm-payment`,
      method: "POST",
      ...params,
    });

  markAccountingFinished = (
    id: number,
    data: IMarkAccountingFinishedRequest,
    params?: RequestParams,
  ) =>
    this.http.request<IOrderDetails>({
      path: `/order/${id}/mark-accounting-finished`,
      method: "POST",
      body: data,
      ...params,
    });

  getPaymentConfirmingUsers = (query: any, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<any>>({
      path: `/users/payment-confirming-users`,
      method: "GET",
      query,
      ...params,
    });

  updateDeliveryMode = (id: number, data: IUpdateDeliveryMode, params?: RequestParams) =>
    this.http.request<IOrderDetails>({
      path: `/order/${id}/update-delivery-mode`,
      method: "POST",
      body: data,
      ...params,
    });

  updateCustomerSatisfaction = (
    id: number,
    data: IUpdateOrderCustomerSatisfactionRequest,
    params?: RequestParams,
  ) =>
    this.http.request<IOrderCustomerSatisfaction>({
      path: `/order/${id}/customer-satisfaction`,
      method: "PUT",
      body: data,
      ...params,
    });

  getCustomerSatisfactionAudits = (
    id: number,
    query: ICustomerSatisfactionAuditQuery,
    params?: RequestParams,
  ) =>
    this.http.request<IBaseListingResponse<ICustomerSatisfactionAudit>>({
      path: `/order/${id}/customer-satisfaction/audits`,
      method: "GET",
      query,
      ...params,
    });

}

export default OrderService;
